import type { ItemDto } from 'cart/services/graphql.types'
import { IconDone } from 'icons/components/IconDone'
import { IconErrorL } from 'icons/components/IconErrorL'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { SnackbarPosition } from 'notifications/constants/constants'
import { useNotificationsContext } from 'notifications/hooks/useNotificationsContext/useNotificationsContext'
import type { MouseEvent } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import {
	ADD_NOTIFICATION_STORE_TIME,
	ADD_NOTIFICATION_TIME,
	ADD_NOTIFICATION_TIME_TEST,
} from '../constants/constants'
import { AddedToCartNotificationContent } from '../context/AddedToCartNotificationContent/AddedToCartNotificationContent'
import { useAnalytics } from './useAnalytics'

export const useNotifications = () => {
	const { addNotification, removeNotification } = useNotificationsContext()
	const { t } = useLabels()
	const { sendBagUpdateEvent } = useAnalytics()
	const { isLessThanLarge } = useResponsive()
	const addToBagLabel = t('snackbar.addToBag.confirmation.text')

	return {
		removeNotification,
		addItemNotification: () =>
			addNotification({
				id: 'addItemNotification',
				title: addToBagLabel,
				delay: ADD_NOTIFICATION_STORE_TIME,
				icon: <IconDone color='content-inverse' />,
				position: SnackbarPosition.TOP,
			}),
		addItemTextNotification: () =>
			addNotification({
				id: 'addItemNotification',
				title: addToBagLabel,
				delay: ADD_NOTIFICATION_STORE_TIME,
				position: SnackbarPosition.BOTTOM,
			}),
		addItemNotificationError: () =>
			addNotification({
				id: 'addItemNotificationError',
				title: t('snackbar.addToBag.error.text'),
				delay: ADD_NOTIFICATION_STORE_TIME,
				icon: <IconErrorL color='content-inverse' />,
				position: SnackbarPosition.BOTTOM,
			}),
		itemWithoutStockNotification: () =>
			addNotification({
				id: 'itemWithoutStockNotification',
				title: t('shoppingBag.error.noStock.title'),
				subtitle: t('shoppingBag.error.noStock.bodycopy'),
				delay: ADD_NOTIFICATION_STORE_TIME,
				icon: <IconErrorL color='content-inverse' />,
				position: SnackbarPosition.TOP,
			}),
		updateNotification: () => {
			sendBagUpdateEvent()
			addNotification({
				id: 'updateNotification',
				title: t('snackbar.updatedBag.title'),
				subtitle: t('snackbar.updatedBag.bodycopy'),
				delay: ADD_NOTIFICATION_TIME,
				position: isLessThanLarge
					? SnackbarPosition.BOTTOM
					: SnackbarPosition.TOP,
			})
		},
		itemWithParamErrorNotification: () =>
			addNotification({
				id: 'itemWithParamErrorNotification',
				title: t('shoppingBag.error.connection.title'),
				subtitle: t('shoppingBag.error.connection.bodycopy'),
				delay: ADD_NOTIFICATION_STORE_TIME,
				icon: <IconErrorL color='content-inverse' />,
				position: SnackbarPosition.TOP,
			}),
		unexpectedErrorNotification: () =>
			addNotification({
				id: 'unexpectedError',
				title: t('shoppingBag.snackbar.genericError.text'),
				delay: ADD_NOTIFICATION_STORE_TIME,
				icon: <IconErrorL color='content-inverse' />,
				position: SnackbarPosition.TOP,
			}),
		deletedItemNotification: ({ onClick }: { onClick: () => void }) =>
			addNotification({
				onClick: () => {
					onClick()
					removeNotification()
				},
				id: 'deletedItemNotification',
				title: t('shoppingBag.deletedItem.snackbar.title'),
				delay: ADD_NOTIFICATION_STORE_TIME,
				buttonText: t('snackbar.undo.button'),
				position: SnackbarPosition.BOTTOM,
			}),
		editedItemErrorNotification: ({
			onClick,
		}: {
			onClick: (e: MouseEvent<HTMLButtonElement>) => void
		}) =>
			addNotification({
				onClick,
				id: 'editedItemErrorNotification',
				title: t('snackbar.edit.error.text'),
				delay: ADD_NOTIFICATION_STORE_TIME,
				buttonText: t('snackbar.edit.error.button'),
				position: SnackbarPosition.BOTTOM,
			}),
		addLargeNotification: ({
			childrenProps,
		}: {
			childrenProps: {
				item: ItemDto
				cartItems: ItemDto[]
				isSmall: boolean
				closeFeedbackEvent: ({
					location,
				}: {
					location: 'in' | 'out' | 'swipe'
				}) => void
			}
		}) =>
			addNotification({
				id: 'addItemNotification',
				title: t('notification.addToBag.title'),
				delay: ADD_NOTIFICATION_TIME_TEST,
				isSmall: childrenProps.isSmall,
				closeFeedbackEvent: childrenProps.closeFeedbackEvent,
				children: (
					<AddedToCartNotificationContent
						item={childrenProps.item}
						cartItems={childrenProps.cartItems}
					/>
				),
				type: 'large',
				position: SnackbarPosition.BOTTOM,
			}),
	}
}
